import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import TagsInput from 'react-tagsinput'

import { tagsRegexp, validateCompetitors } from 'Pods/Shared/Rules/Rules'
import Icon from 'Pods/Shared/Icon/Icon'
import Tooltip from 'Pods/Shared/Tooltip'

class TagsInputField extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      recommendedTags: props.recommendedTags,
      hasMaxError: props.max > 0 && props.value.length > props.max,
      hasCompetitorError: false,
      competitorErrorText: '',
      isTooLong: false,
    }

    this.onTagsChange = this.onTagsChange.bind(this)
    this.onRecommendedTagClick = this.onRecommendedTagClick.bind(this)
    this.renderTagsInputLayout = this.renderTagsInputLayout.bind(this)
    this.validateTagsLength = this.validateTagsLength.bind(this)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.recommendedTags !== this.props.recommendedTags) {
      this.setState({ recommendedTags: this.props.recommendedTags })
    }
  }

  onTagsChange(tags) {
    const textWithCompetitors = validateCompetitors(tags.join())
    const hasError = !!textWithCompetitors
    const errorText = textWithCompetitors && textWithCompetitors('Tags')
    const tagTooLong = this.validateTagsLength(tags)

    this.setState({
      hasMaxError: this.props.max > 0 && tags.length > this.props.max,
      hasCompetitorError: hasError,
      competitorErrorText: errorText,
      isTooLong: tagTooLong,
    })

    this.props.onChange(tags, hasError || tagTooLong)
  }

  validateTagsLength(tags) {
    let isTooLong = false

    tags.forEach(tag => {
      if (tag.length > 30) {
        isTooLong = true
      }
    })

    return isTooLong
  }

  onRecommendedTagClick(tag) {
    const tagsArray = this.props.value

    this.setState(prevState => ({
      recommendedTags: prevState.recommendedTags.filter(recTag => recTag !== tag),
    }))

    if (!tagsArray.includes(tag)) {
      tagsArray.push(tag)
      this.onTagsChange(tagsArray)
    }
  }

  renderTagsInputLayout(tagComponents, inputComponent) {
    return (
      <Fragment>
        <Icon type={['far', this.props.tagIcon]} color={this.props.tagIconColor} className="u-mr10 u-ml10" />
        {tagComponents}
        {inputComponent}
      </Fragment>
    )
  }

  render() {
    const inputErrorClass = this.state.hasMaxError || this.state.hasCompetitorError || this.state.isTooLong || (this.props.showError && this.props.errorText !== '') ? 'is-invalid' : ''

    return (
      <div className={`form-group form-group-grey ${this.props.formClassName}`}>
        {this.props.label.length > 0
          && (
            <label htmlFor={this.props.id}>
              {this.props.label}{this.props.required && <b> *</b>}

              {this.props.moreInfo !== ''
                && (
                  <Tooltip
                    tooltipClassName="tooltip--medium tooltip--spaced u-text-center"
                    id="tags-input-tooltip"
                    content={this.props.moreInfo}
                    randomiseID
                  >
                    <Icon type={['fas', 'question-circle']} color="grey" className="info-icon ml-5" />
                  </Tooltip>
                )
              }
              {this.props.customMoreInfo}
            </label>
          )
        }

        <TagsInput
          value={this.props.value}
          onChange={this.onTagsChange}
          onlyUnique
          addOnPaste
          addKeys={[32, 13]}
          validationRegex={tagsRegexp}
          className={`react-tagsinput form-control form-control-grey ${inputErrorClass}`}
          inputProps={{ placeholder: this.props.placeholder, id: this.props.id }}
          pasteSplit={data => { return data.replace(/[\r\n,;]/g, ' ').split(' ').map(d => d.trim()) }}
          renderLayout={this.renderTagsInputLayout}
          renderInput={this.props.renderInput}
        />

        {this.props.recommendedTags.length > 0 && !this.props.hideRecommendedTags
          && (
            <div className="recommended-tags">
              <span className="text-semibold">Recommended tags:</span>
              {this.state.recommendedTags.map(tag => <div key={tag} className="filter-pill" onClick={() => this.onRecommendedTagClick(tag)}>{tag}</div>)}
            </div>
          )
        }

        {this.state.hasCompetitorError
          && <div className="error-message"><span className="text">{this.state.competitorErrorText}</span></div>
        }

        {(this.props.showError || this.state.hasMaxError) && (
          <div className="error-message"><span className="text">{this.props.errorText}</span></div>
        )}

        {(this.state.isTooLong) && (
          <div className="error-message">
            <span className="text">
              Tag can't be longer than 30 characters. Please separate tags with space.
            </span>
          </div>
        )}
      </div>
    )
  }
}

TagsInputField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  formClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  recommendedTags: PropTypes.array,
  hideRecommendedTags: PropTypes.bool,
  max: PropTypes.number,
  showError: PropTypes.bool,
  tagIcon: PropTypes.string,
  tagIconColor: PropTypes.string,
  errorText: PropTypes.string,
  moreInfo: PropTypes.string,
  customMoreInfo: PropTypes.node,
}

TagsInputField.defaultProps = {
  label: '',
  required: false,
  placeholder: '',
  formClassName: '',
  recommendedTags: [],
  hideRecommendedTags: false,
  max: -1,
  showError: false,
  tagIcon: 'tag',
  tagIconColor: 'green-dark',
  errorText: '',
  moreInfo: '',
  customMoreInfo: null,
}

export default TagsInputField
